import React, { useState } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, BarElement, Title, Tooltip, Legend, Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useStore } from 'context/StoreContext';
import { useApi } from 'hooks/useApi';
import { DashboardService } from 'services/analytics/dashboard.service';
import { StoreDataService } from 'services/data/store-data.service';
import { DataHealth } from 'dtos/data/data-health.dto';
import { Card, CardHeader } from 'components/common/Card';
import { EstimatedAmount } from 'components/common/EstimatedAmount';
import { EmptyState, LoadingScreen } from 'components/common/Spinner';
import { SeverityBadge } from 'components/common/Badge';
import { Button } from 'components/common/Button';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, formatPercent, formatChange, formatShortDate } from 'utils/formatters';
import { TrendingUp, TrendingDown, DollarSign, AlertTriangle, ArrowRight, CheckCircle, Circle, Upload, Camera, ShieldCheck } from 'lucide-react';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

function KpiCard({ label, value, change, hint, icon: Icon }: { label: string; value: string; change?: number; hint?: string; icon: React.ElementType }) {
  const hasChange = typeof change === 'number' && Number.isFinite(change);
  const isUp = (change ?? 0) >= 0;
  const Trend = isUp ? TrendingUp : TrendingDown;
  return (
    <Card>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
          <p className="mt-1 text-xl font-bold text-gray-900 sm:text-2xl">{value}</p>
          {hasChange && (
            <p className={`mt-1 flex items-center gap-1 text-xs ${isUp ? 'text-green-600' : 'text-red-600'}`}>
              <Trend size={12} />{formatChange(change!)} {hint ?? 'vs previous period'}
            </p>
          )}
        </div>
        <div className="hidden rounded-lg bg-blue-50 p-2 sm:block"><Icon size={20} className="text-blue-600" /></div>
      </div>
    </Card>
  );
}

// Shown above the numbers when a feed is missing or old, so a quiet dashboard is
// never mistaken for "no problems".
function DataHealthNotice({ health }: { health: DataHealth }) {
  const navigate = useNavigate();
  const feeds = [
    { name: 'Sales', feed: health.sales },
    { name: 'Supplier costs', feed: health.costs },
    { name: 'Prices', feed: health.price_book },
  ].filter(({ feed }) => feed.status !== 'ok');
  if (!feeds.length) return null;
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between">
      <div className="space-y-0.5">
        {feeds.map(({ name, feed }) => <p key={name}><strong>{name}:</strong> {feed.message}</p>)}
        <p className="text-xs text-amber-800">Until this is fixed, Pellvr may miss problems in your store.</p>
      </div>
      <Button size="sm" variant="secondary" onClick={() => navigate('/data')}>Fix store data</Button>
    </div>
  );
}

function GettingStarted({ hasSales, hasCosts }: { hasSales: boolean; hasCosts: boolean }) {
  const navigate = useNavigate();
  const steps = [
    { done: true, title: 'Create your account', body: 'Your 14-day free trial has started.' },
    { done: hasSales, title: 'Bring in your sales', body: 'Upload a sales export from your POS, or connect Pellvr to your back-office PC to send it automatically.', action: <Button size="sm" onClick={() => navigate('/data')}><Upload size={14} /> Set up store data</Button> },
    { done: hasCosts, title: 'Add your supplier costs', body: 'Snap a photo of a vendor invoice or receipt, or upload a cost list. Pellvr reads the costs and matches them to your products.', action: <Button size="sm" variant="secondary" onClick={() => navigate('/costs/capture')}><Camera size={14} /> Capture an invoice</Button> },
    { done: false, title: 'See where you\'re losing money', body: 'Pellvr checks every item for cost increases you haven\'t passed on, low margins, and shrinking profit — and tells you what to charge.' },
  ];
  return (
    <Card>
      <CardHeader title="Get your first profit check" subtitle="Most stores finish these steps in under 30 minutes." />
      <ol className="space-y-4">
        {steps.map((step, index) => (
          <li key={step.title} className="flex gap-3">
            {step.done ? <CheckCircle size={20} className="mt-0.5 flex-shrink-0 text-green-600" /> : <Circle size={20} className="mt-0.5 flex-shrink-0 text-gray-300" />}
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-semibold ${step.done ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{index + 1}. {step.title}</p>
              {!step.done && <p className="mt-0.5 text-sm text-gray-600">{step.body}</p>}
              {!step.done && step.action && <div className="mt-2">{step.action}</div>}
            </div>
          </li>
        ))}
      </ol>
    </Card>
  );
}

export default function DashboardPage() {
  const { currentStore, org } = useStore();
  const navigate = useNavigate();
  const [period, setPeriod] = useState<'7d' | '30d'>('30d');

  const { data, loading, error, refetch } = useApi(
    () => DashboardService.getSummary(org?.id ?? '', currentStore?.id ?? '', period),
    [currentStore?.id, period]
  );
  const { data: coverage } = useApi(
    () => DashboardService.getCoverage(org?.id ?? '', currentStore?.id ?? '', period),
    [currentStore?.id, period]
  );

  const { data: health } = useApi(
    () => StoreDataService.health(org?.id ?? '', currentStore?.id ?? ''),
    [currentStore?.id]
  );

  if (loading && !data) return <LoadingScreen />;
  if (error || !data) {
    return <EmptyState icon={<AlertTriangle size={40} />} title="We couldn't load your dashboard" message={error ?? undefined} action={<Button variant="secondary" onClick={refetch}>Try again</Button>} />;
  }

  const hasSales = data.sales_by_day.some(day => day.revenue !== 0);
  const hasCosts = (coverage?.valid_cost_coverage_pct ?? 0) > 0;
  const found = data.recovered_or_found;
  const openImpact = found?.open_leak_monthly_impact ?? data.estimated_monthly_impact;
  const openCount = found?.open_leak_count ?? data.profit_leak_count;

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 className="text-xl font-bold text-gray-900">{currentStore?.name}</h1>
        <p className="text-sm text-gray-500">Profit overview</p>
      </div>
      {hasSales && (
        <div className="flex gap-1 rounded-lg bg-gray-100 p-0.5">
          {(['7d', '30d'] as const).map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${period === p ? 'bg-white text-gray-900 shadow' : 'text-gray-500 hover:text-gray-700'}`}>
              {p === '7d' ? 'Last 7 days' : 'Last 30 days'}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  if (!hasSales) {
    return (
      <div className="space-y-5">
        {header}
        <GettingStarted hasSales={false} hasCosts={hasCosts} />
        <Card>
          <CardHeader title="Sales, profit and margin trends" subtitle="Daily sales, gross profit and margin" />
          <div className="flex h-40 items-center justify-center rounded-md border border-dashed border-gray-200 bg-gray-50 px-4 text-center text-sm text-gray-500">
            Your trends appear here after your first sales upload.
          </div>
        </Card>
      </div>
    );
  }

  const chartData = {
    labels: data.sales_by_day.map(d => formatShortDate(d.date)),
    datasets: [
      { label: 'Sales', data: data.sales_by_day.map(d => d.revenue), borderColor: '#245743', backgroundColor: 'rgba(36,87,67,0.08)', fill: true, tension: 0.3, pointRadius: 2 },
      { label: 'Gross profit', data: data.sales_by_day.map(d => d.gross_profit), borderColor: '#16a34a', backgroundColor: 'transparent', tension: 0.3, pointRadius: 2, borderDash: [4, 2] },
    ],
  };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' as const, labels: { font: { size: 11 } } },
      tooltip: { mode: 'index' as const, callbacks: { label: (context: any) => `${context.dataset.label}: ${formatCurrency(Number(context.parsed.y), 2)}` } },
    },
    scales: { y: { ticks: { callback: (v: any) => formatCurrency(Number(v)) } } },
  };
  // Margin is over sales of items with a cost, so missing costs don't read as a margin drop.
  const marginDays = data.sales_by_day.filter(day => (day.costed_revenue ?? 0) > 0);
  const marginData = {
    labels: marginDays.map(d => formatShortDate(d.date)),
    datasets: [{ label: 'Gross margin', data: marginDays.map(d => (d.gross_profit / d.costed_revenue!) * 100), borderColor: '#2563eb', backgroundColor: 'rgba(37,99,235,0.08)', fill: true, tension: 0.3, pointRadius: 2 }],
  };
  const marginOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: (context: any) => `Gross margin: ${Number(context.parsed.y).toFixed(1)}%` } } },
    scales: { y: { ticks: { callback: (v: any) => `${Number(v).toFixed(0)}%` } } },
  };
  const maxCategoryRevenue = Math.max(...data.top_categories.map(cat => cat.revenue), 1);

  return (
    <div className="space-y-5">
      {header}
      {health && <DataHealthNotice health={health} />}

      {/* The headline: money at stake and what to do */}
      <Card className={openCount > 0 ? 'border-orange-200' : 'border-green-200'}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className={`rounded-lg p-2 ${openCount > 0 ? 'bg-orange-50' : 'bg-green-50'}`}>
              {openCount > 0 ? <AlertTriangle size={22} className="text-orange-500" /> : <ShieldCheck size={22} className="text-green-600" />}
            </div>
            <div>
              {openCount > 0 ? (
                <>
                  <p className="text-sm text-gray-600">Pellvr found <strong className="text-gray-900">{openCount} profit leak{openCount === 1 ? '' : 's'}</strong> costing you about</p>
                  <p className="text-3xl font-bold text-gray-900">{formatCurrency(openImpact)}<span className="text-base font-medium text-gray-500"> / month</span></p>
                </>
              ) : (
                <>
                  <p className="text-lg font-semibold text-gray-900">No open profit leaks</p>
                  <p className="text-sm text-gray-600">Pellvr checks your prices against your latest costs every night.</p>
                </>
              )}
              {(found?.resolved_leak_count_30d ?? 0) > 0 && (
                <p className="mt-1 text-sm text-green-700">You fixed {found!.resolved_leak_count_30d} leak{found!.resolved_leak_count_30d === 1 ? '' : 's'} worth ~{formatCurrency(found!.resolved_leak_monthly_impact_30d)}/month in the last 30 days.</p>
              )}
            </div>
          </div>
          {openCount > 0 && <Button onClick={() => navigate('/alerts')}>Fix them now <ArrowRight size={14} /></Button>}
        </div>
      </Card>

      {coverage && coverage.available && coverage.warning && (
        <Card className="border-amber-200 bg-amber-50">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="mt-0.5 text-amber-600" />
            <div>
              <p className="text-sm font-semibold text-amber-900">Some items are missing costs</p>
              <p className="mt-1 text-sm text-amber-800">
                We have costs for {coverage.valid_cost_coverage_pct?.toFixed(0)}% of your sales. Profit numbers and leak alerts only cover items with a cost. <button className="font-semibold underline" onClick={() => navigate('/costs')}>Add missing costs</button>
              </p>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiCard label="Sales" value={formatCurrency(data.revenue)} change={data.revenue_change_pct} icon={DollarSign} />
        <KpiCard label="Gross profit" value={formatCurrency(data.gross_profit)} change={data.gross_profit_change_pct} icon={TrendingUp} />
        <KpiCard label="Gross margin" value={formatPercent(data.gross_margin)} change={data.gross_margin_change_pct} hint="pts vs previous period" icon={TrendingUp} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card padding={false}>
          <div className="flex items-center justify-between border-b border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-900">Biggest leaks to fix first</h3>
            <button onClick={() => navigate('/alerts')} className="flex items-center gap-1 text-xs text-blue-600 hover:underline">View all <ArrowRight size={10} /></button>
          </div>
          {data.top_profit_leaks.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-gray-500">Nothing to fix right now.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {data.top_profit_leaks.map((leak, i) => (
                <button key={leak.alert_id} className="flex w-full items-start gap-3 px-5 py-3 text-left hover:bg-gray-50" onClick={() => navigate('/alerts')}>
                  <span className="w-4 text-sm font-bold text-gray-400">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">{leak.title}</p>
                    {leak.recommended_action && <p className="mt-0.5 text-xs text-gray-600">{leak.recommended_action}</p>}
                    <EstimatedAmount amount={leak.estimated_monthly_impact} className="mt-0.5 text-xs" />
                  </div>
                  <SeverityBadge severity={leak.severity} />
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card className="lg:col-span-2" padding={false}>
          <div className="border-b border-gray-100 p-5">
            <CardHeader title="Sales and profit" subtitle={`Last ${period === '7d' ? '7' : '30'} days`} />
          </div>
          <div className="h-56 p-5"><Line data={chartData} options={chartOptions} /></div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card padding={false}>
          <div className="border-b border-gray-100 p-5">
            <CardHeader title="Gross margin trend" subtitle="Daily, on items with a known cost" />
          </div>
          {marginDays.length > 1
            ? <div className="h-56 p-5"><Line data={marginData} options={marginOptions} /></div>
            : <p className="px-5 py-10 text-center text-sm text-gray-500">Add supplier costs to see your margin by day. <button className="font-semibold text-blue-600 hover:underline" onClick={() => navigate('/costs')}>Add costs</button></p>}
        </Card>

      {data.top_categories.length > 0 && (
        <Card>
          <CardHeader title="Top categories" subtitle="Sales and gross margin" />
          <div className="space-y-3">
            {data.top_categories.map(cat => (
              <div key={cat.category} className="flex items-center gap-3">
                <span className="w-24 flex-shrink-0 truncate text-sm text-gray-700 sm:w-32">{cat.category}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
                  <div className="h-full rounded-full bg-blue-500" style={{ width: `${(cat.revenue / maxCategoryRevenue) * 100}%` }} />
                </div>
                <span className="w-20 text-right text-sm font-medium text-gray-900">{formatCurrency(cat.revenue)}</span>
                <span className="w-12 text-right text-xs text-gray-500">{formatPercent(cat.margin)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
      </div>

      {!hasCosts && <GettingStarted hasSales hasCosts={false} />}
    </div>
  );
}
